import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ReportGenerationApiResponse } from '../../../shared/model/api-response.model';

export enum ApplicantSearchReports {
  Demographics = '/Applicant/GetApplicantSearchDemographicsReport',
  ActiveStatusSummary = '/Applicant/GetApplicantSearchActiveStatusSummaryReport',
  Sourcing = '/Applicant/GetApplicantSearchSourcingReport',
  OfferToHire = '/Applicant/GetApplicantSearchOfferToHireReport',
  TimeToHire = '/Applicant/GetApplicantSearchTimeToHireReport',
}

@Injectable({
  providedIn: 'root',
})
export class ApplicantService {
  http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  getPersonalInfo() {
    const url = '/Applicant/GetPersonalInfo';
    return this.http.get(url).toPromise();
  }

  getStagesByProfileID(onboardProfileID?: number) {
    const url = '/Applicant/GetStagesByProfileID?onboardProfileID=' + onboardProfileID;
    return this.http.get(url).toPromise();
  }

  getReqColumns(reqForm: string) {
    const url = '/Applicant/GetReqColumns?reqForm=' + reqForm;
    return this.http.get(url).toPromise();
  }

  getRecruiterList() {
    const url = '/Applicant/GetRecruiterList';
    return this.http.get(url).toPromise();
  }

  getApplicantsByJobReq(requestParam: string) {
    const url = '/Applicant/GetApplicantsByJobReq?' + requestParam;
    return this.http.get(url).toPromise();
  }

  getJobReqExport(
    reqStageID: string,
    status: string,
    startDate: string,
    endDate: string,
    searchString: string,
    district: string,
    filename: string
  ): Observable<ReportGenerationApiResponse> {
    const httpParams = new HttpParams()
      .set('reqStageID', reqStageID)
      .set('status', status)
      .set('startDate', startDate)
      .set('endDate', endDate)
      .set('searchString', searchString)
      .set('district', district)
      .set('filename', filename);
    return this.http.get<ReportGenerationApiResponse>('/Applicant/GetJobReqExport', { params: httpParams });
  }

  getApplicantSearchReportFull(requestObj: any) {
    let url =
      '/Applicant/getApplicantSearchReportFull?file=' +
      requestObj.file +
      '&filename=' +
      requestObj.filename +
      '&district=' +
      requestObj.district +
      '&appStage=' +
      requestObj.appStage +
      '&reqID=' +
      requestObj.reqID;
    if (requestObj.beginDate != null) {
      url += '&beginDate=' + requestObj.beginDate;
    }
    if (requestObj.endDate != null) {
      url += '&endDate=' + requestObj.endDate;
    }
    return this.http.get(url).toPromise();
  }

  getApplicantSearchReport(applicantSearchReport: ApplicantSearchReports, requestObj: any): Observable<ReportGenerationApiResponse> {
    const httpParams = new HttpParams()
      .set('district', requestObj.district)
      .set('appStage', requestObj.appStage)
      .set('reqID', requestObj.reqID);
    if (requestObj.beginDate != null) {
      httpParams.set('beginDate=', requestObj.beginDate);
    }
    if (requestObj.endDate != null) {
      httpParams.set('endDate=', requestObj.endDate);
    }
    return this.http.get<ReportGenerationApiResponse>(applicantSearchReport, { params: httpParams });
  }

  getStages(reqID?: any, onboardID?: any) {
    const url = '/Applicant/GetStages?reqID=' + reqID + '&onboardID=' + onboardID;
    return this.http.get(url).toPromise();
  }

  getReqDetails(reqID?: any, reqApplyToken?: any) {
    const url = '/Applicant/GetReqDetails?reqID=' + reqID + '&reqApplyToken=' + reqApplyToken;
    return this.http.get(url).toPromise();
  }

  reqSpecQuesAnswerGet(reqID?: any, onboardID?: any) {
    const url = '/Applicant/ReqSpecQuesAnswerGet?reqID=' + reqID + '&onboardID=' + onboardID;
    return this.http.get(url).toPromise();
  }

  getPersonalInfoForDate(onboardID: string) {
    const url = '/Applicant/GetPersonalInfoForDate?onboardID=' + onboardID;
    return this.http.get(url).toPromise();
  }

  updateDispReason(onboardID: string, dispReason: string) {
    return this.http
      .post('/Applicant/UpdateDispReason', {
        onboardID: onboardID,
        dispReason: dispReason,
      })
      .toPromise();
  }

  personalInfoEdit(data: any) {
    return this.http
      .post('/Applicant/PersonalInfoEdit', {
        onboardID: data.onboardID,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        address1: data.address1,
        address2: data.address2,
        city: data.city,
        state: data.state,
        cell: data.cell,
        zip: data.zip,
        contact: data.contact,
        contactSource: data.contactSource,
        contactSourceNotes: data.contactSourceNotes,
        hireDateStr: data.hireDateStr,
        country: data.country,
        midName: data.midName,
        suffix: data.suffix,
        hRRMKeyID: data.hRRMKeyID,
        birthdayStr: data.birthdayStr,
        maritalStatus: data.maritalStatus,
        countryOther: data.countryOther,
        isManagerEdit: data.isManagerEdit,
        reservedHRRef: data.reservedHRRef,
      })
      .toPromise();
  }

  applicantHistoryGet(reqApplicantID?: any, reqID?: any) {
    const url = '/Applicant/ApplicantHistoryGet?reqApplicantID=' + reqApplicantID + '&reqID=' + reqID;
    return this.http.get(url).toPromise();
  }

  getApplicantPrevReqs(onboardID?: any) {
    const url = '/Applicant/GetApplicantPrevReqs?onboardID=' + onboardID;
    return this.http.get(url).toPromise();
  }

  applicantChangeStage(reqApplicantIDString: string, reqStageID?: number) {
    return this.http
      .post('/Applicant/ApplicantChangeStage', {
        reqApplicantIDString: reqApplicantIDString,
        reqStageID: reqStageID,
      })
      .toPromise();
  }

  setDateHired(onboardID: string, hireDateStr: string) {
    return this.http
      .post('/Applicant/SetDateHired', {
        onboardID: onboardID,
        hireDateStr: hireDateStr,
      })
      .toPromise();
  }

  getAssignedRecruiters(reqID?: any) {
    const url = '/Applicant/GetAssignedRecruiters?reqID=' + reqID;
    return this.http.get(url).toPromise();
  }

  getReqGrid(status: string, startDateStr?: string, endDateStr?: string, recruiterUserKeyIDString?: string) {
    let url = '/Applicant/GetReqGrid?status=' + status;
    if (startDateStr != null) {
      url += '&startDateStr=' + startDateStr;
    }
    if (endDateStr != null) {
      url += '&endDateStr=' + endDateStr;
    }
    if (recruiterUserKeyIDString != null) {
      url += '&recruiterUserKeyIDString=' + recruiterUserKeyIDString;
    }
    return this.http.get(url).toPromise();
  }

  reqCopyMove(onboardID?: any, isCopy?: boolean, moveToReqID?: any, onboardIDArray?: any[]) {
    return this.http
      .post('/Applicant/ReqCopyMove', {
        onboardID: onboardID,
        isCopy: isCopy,
        moveToReqID: moveToReqID,
        onboardIDArray: onboardIDArray,
      })
      .toPromise();
  }

  applicantRatingGet(reqApplicantID?: any) {
    const url = '/Applicant/ApplicantRatingGet?reqApplicantID=' + reqApplicantID;
    return this.http.get(url).toPromise();
  }

  applicantRatingAddEdit(reqApplicantID?: any, ratingValue?: string, reqID?: any, reqApplicantRatingID?: any, ratingStage?: string) {
    return this.http
      .post('/Applicant/ApplicantRatingAddEdit', {
        reqApplicantID: reqApplicantID,
        ratingValue: ratingValue,
        reqID: reqID,
        reqApplicantRatingID: reqApplicantRatingID,
        ratingStage: ratingStage,
      })
      .toPromise();
  }

  applicantRatingRemove(reqApplicantID?: any, reqApplicantRatingID?: number) {
    return this.http
      .post('/Applicant/ApplicantRatingRemove', {
        reqApplicantID: reqApplicantID,
        reqApplicantRatingID: reqApplicantRatingID,
      })
      .toPromise();
  }

  setVeteranReview(onboardID?: any, isRecommended?: boolean) {
    return this.http
      .post('/Applicant/SetVeteranReview', {
        onboardID: onboardID,
        isRecommended: isRecommended,
      })
      .toPromise();
  }

  applicantToOnboard(onboardID?: any, onboardProfileID?: number, reqID?: any) {
    return this.http
      .post('/Applicant/ApplicantToOnboard', {
        onboardID: onboardID,
        onboardProfileID: onboardProfileID,
        reqID: reqID,
      })
      .toPromise();
  }

  deleteApplicant(onboardID?: any) {
    return this.http
      .post('/Applicant/DeleteApplicant', {
        onboardID: onboardID,
      })
      .toPromise();
  }

  sendBulkEmail(requestObj?: any) {
    return this.http
      .post('/Applicant/SendBulkEmail', {
        toEmail: requestObj.toEmail,
        ccEmail: requestObj.ccEmail,
        bccEmail: requestObj.bccEmail,
        subject: requestObj.subject,
        body: requestObj.body,
        newFilenames: requestObj.newFilenames,
        newFiles: requestObj.newFiles,
        onboardID: requestObj.onboardID,
        reqID: requestObj.reqID,
        emailTemplateID: requestObj.emailTemplateID,
        includeICS: requestObj.includeICS,
        icsStartDate: requestObj.icsStartDate,
        icsEndDate: requestObj.icsEndDate,
        location: requestObj.location,
        sendEmail: requestObj.sendEmail,
        templateTo: requestObj.templateTo,
        mailFrom: requestObj.mailFrom,
        queueType: requestObj.queueType,
        notificationMode: requestObj.notificationMode,
        sMSTo: requestObj.sMSTo,
      })
      .toPromise();
  }

  sendEmail(requestObj?: any) {
    return this.http
      .post('/Applicant/SendEmail', {
        toEmail: requestObj.toEmail,
        ccEmail: requestObj.ccEmail,
        bccEmail: requestObj.bccEmail,
        subject: requestObj.subject,
        body: requestObj.body,
        newFilenames: requestObj.newFilenames,
        newFiles: requestObj.newFiles,
        includeICS: requestObj.includeICS,
        icsStartDate: requestObj.icsStartDate,
        icsEndDate: requestObj.icsEndDate,
        location: requestObj.location,
        onboardID: requestObj.onboardID,
        reqID: requestObj.reqID,
        mailFrom: requestObj.mailFrom,
        queueType: requestObj.queueType,
        notificationMode: requestObj.notificationMode,
        sMSTo: requestObj.sMSTo,
      })
      .toPromise();
  }

  getReqTemplate(reqTemplateID?: number, onboardProfileID?: number) {
    const url = '/Applicant/GetReqTemplate?reqTemplateID=' + reqTemplateID + '&onboardProfileID=' + onboardProfileID;
    return this.http.get(url).toPromise();
  }

  reqTemplateDelete(reqTemplateID?: number) {
    return this.http
      .post('/Applicant/ReqTemplateDelete', {
        reqTemplateID: reqTemplateID,
      })
      .toPromise();
  }

  reqTemplateAddEdit(requestObj: any) {
    return this.http.post('/Applicant/ReqTemplateAddEdit', requestObj).toPromise();
  }

  getNextReqNum() {
    const url = '/Applicant/GetNextReqNum';
    return this.http.get(url).toPromise();
  }

  getStates(countryCode?: string) {
    const url = '/Applicant/GetStates?countryCode=' + countryCode;
    return this.http.get(url).toPromise();
  }

  removeRecruiter(reqID?: any, reqRecruiterID?: number) {
    return this.http
      .post('/Applicant/RemoveRecruiter', {
        reqID: reqID,
        reqRecruiterID: reqRecruiterID,
      })
      .toPromise();
  }

  addEditRequisition(
    reqID?: any,
    onboardProfileID?: number,
    reqTemplateID?: number,
    positionTitle?: string,
    positionDesc?: string,
    positionRequirements?: string,
    positionInstructions?: string,
    positionNotes?: string,
    city?: string,
    state?: string,
    district?: string,
    openingCount?: number,
    reqStatus?: number,
    veteranRevUserKeyID?: number,
    veteranRev2UserKeyID?: number,
    salaryInterval?: string,
    country?: string,
    eEOCategory?: string,
    jCJMKeyID?: number,
    anticipCloseDateStr?: string,
    reqNum?: number,
    unpostDateStr?: string,
    onboardLocationID?: number,
    payRangeLow?: number,
    payRangeHigh?: number
  ) {
    return this.http
      .post('/Applicant/AddEditRequisition', {
        reqID: reqID,
        onboardProfileID: onboardProfileID,
        reqTemplateID: reqTemplateID,
        positionTitle: positionTitle,
        positionDesc: positionDesc,
        positionRequirements: positionRequirements,
        positionInstructions: positionInstructions,
        positionNotes: positionNotes,
        city: city,
        state: state,
        district: district,
        openingCount: openingCount,
        reqStatus: reqStatus,
        veteranRevUserKeyID: veteranRevUserKeyID,
        veteranRev2UserKeyID: veteranRev2UserKeyID,
        salaryInterval: salaryInterval,
        country: country,
        eEOCategory: eEOCategory,
        jCJMKeyID: jCJMKeyID,
        anticipCloseDateStr: anticipCloseDateStr,
        reqNum: reqNum,
        unpostDateStr: unpostDateStr,
        onboardLocationID: onboardLocationID,
        payRangeLow: payRangeLow,
        payRangeHigh: payRangeHigh,
      })
      .toPromise();
  }

  addRecruiter(reqID?: any, recruiterUserKeyID?: number) {
    return this.http
      .post('/Applicant/AddRecruiter', {
        reqID: reqID,
        recruiterUserKeyID: recruiterUserKeyID,
      })
      .toPromise();
  }

  reqSpecQuesDelete(reqID?: any, reqSpecQuesID?: number) {
    return this.http
      .post('/Applicant/ReqSpecQuesDelete', {
        reqID: reqID,
        reqSpecQuesID: reqSpecQuesID,
      })
      .toPromise();
  }

  reqSpecQuesAddEdit(reqID?: any, reqSpecQuesID?: number[], questionText?: string[]) {
    return this.http
      .post('/Applicant/ReqSpecQuesAddEdit', {
        reqID: reqID,
        reqSpecQuesID: reqSpecQuesID,
        questionText: questionText,
      })
      .toPromise();
  }

  getRecruiters(searchText: string) {
    const url = '/Applicant/GetRecruiters?searchText=' + searchText;
    return this.http.get(url).toPromise();
  }
  reqAction(reqID: string, reqAction: string) {
    return this.http
      .post('/Applicant/ReqAction', {
        reqID: reqID,
        reqAction: reqAction,
      })
      .toPromise();
  }

  reqApplyTokenGet(reqID: string) {
    const url = '/Applicant/ReqApplyTokenGet?reqID=' + reqID;
    return this.http.get(url).toPromise();
  }

  getJobTemplate(reqTemplateId: string, onboardProfileId: string, reqTemplateName: string): Observable<ReportGenerationApiResponse> {
    const url = `/Applicant/getJobTemplate?ReqTemplateID=${reqTemplateId}&OnboardProfileID=${onboardProfileId}&filename=${reqTemplateName}`;
    return this.http.get<ReportGenerationApiResponse>(url);
  }
}
